import React, { useContext, useEffect, useRef, type ReactNode } from 'react';
import {
  MutationCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { getImpersonationType } from '@leagueplatform/auth-utils';
import { LocaleContext } from '@leagueplatform/locales';
import { SocketAsFetch } from '@leagueplatform/socket-as-fetch';
import { reportAgentPermissionError } from './permissions-error-modal/permissions-error-modal.provider';
import { usePageLifecycleEffect } from '../hooks/use-page-lifecycle-effect';

export const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    async onError(error) {
      const isImpersonationSessionType = await getImpersonationType();

      if (error instanceof Error) {
        const isWSImpersonatorError = error.message.includes('unauthorized');
        const isRESTImpersonatorError = error.message.includes('403');

        const showImpersonationSessionTypeError =
          isImpersonationSessionType &&
          (isRESTImpersonatorError || isWSImpersonatorError);

        if (showImpersonationSessionTypeError) {
          reportAgentPermissionError(
            true,
            undefined,
            isImpersonationSessionType,
          );
        }
      }
    },
  }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 3,
    },
  },
});

export const ReactQueryProviderWrapper = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { locale } = useContext(LocaleContext);
  const prevLocale = useRef(locale);

  /**
   * Global BFCache lifecycle management:
   * - onFreeze: Close all WebSocket connections cleanly (via unload)
   * - onRestore: Recreate fresh WebSocket connections from scratch (same pattern
   *              as config URL changes), then invalidate all queries
   */
  usePageLifecycleEffect(
    {
      label: 'core-bfcache-lifecycle',
      tags: {
        hook: 'react-query-provider',
      },
      onFreeze: () => SocketAsFetch.freezeConnections(),
      onRestore: async () => {
        try {
          // Recreate fresh sockets from scratch (reuses config change logic)
          await SocketAsFetch.restoreConnections();
        } finally {
          // Always invalidate queries, even if socket restore fails.
          // This ensures REST-based queries still refresh (graceful degradation).
          await queryClient.invalidateQueries();
        }
      },
    },
    [],
  );

  useEffect(() => {
    if (locale !== prevLocale.current) {
      SocketAsFetch.updateUserConnectionConfiguration().then(() => {
        queryClient.invalidateQueries();
      });

      prevLocale.current = locale;
    }
  }, [locale]);
  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
};
